import { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import $api from "@/zapi-frontend/shared/api/axios";
import { confirmPollingEnd } from "@/zapi-frontend/shared/lib/confirmPollingEnd";
import { EGenerationStatus, EGenerationTaskType } from "@/zapi-frontend/widgets/ChatIsland/types";
import { getStatusAll } from "@/zapi-frontend/widgets/ChatIsland";
import { usePresentationStore, TSlide, type TPresentation } from "@/zapi-frontend/stores/presentation";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { toast } from "sonner";
import { LS_PRESENTATION_CHAT_STORAGE } from "@/zapi-frontend/shared/local-storage/keys";

export type PresentationChatMessage = {
  role: "user" | "assistant";
  content: string;
  id: string;
};

type PresentationChatHistory = {
  [projectId: string]: PresentationChatMessage[];
};

type PresentationChatLoadingState = {
  loadingMessageId: string | null;
  pendingBotMessage: PresentationChatMessage | null;
  pendingGenerationId: number | null;
};

type PresentationChatLoadingStates = {
  [projectId: string]: PresentationChatLoadingState;
};

interface PresentationChatState {
  chatHistory: PresentationChatHistory;
  loadingStates: PresentationChatLoadingStates;
  getChatHistory: (projectId: string) => PresentationChatMessage[];
  addMessage: (projectId: string, message: PresentationChatMessage) => void;
  clearChatHistory: (projectId: string) => void;
  removeLastMessage: (projectId: string) => void;
  getLoadingState: (projectId: string) => PresentationChatLoadingState;
  setLoadingState: (projectId: string, updates: Partial<PresentationChatLoadingState>) => void;
  resetLoadingState: (projectId: string) => void;
  getMessagesWithPending: (projectId: string) => PresentationChatMessage[];
}

const createDefaultLoadingState = (): PresentationChatLoadingState => ({
  loadingMessageId: null,
  pendingBotMessage: null,
  pendingGenerationId: null
});

export const usePresentationChatStore = create<PresentationChatState>()(
  persist(
    (set, get) => ({
      chatHistory: {},
      loadingStates: {},

      getChatHistory: (projectId: string) => {
        return get().chatHistory[projectId] || [];
      },

      addMessage: (projectId: string, message: PresentationChatMessage) => {
        set((state) => {
          const messages = state.chatHistory[projectId] || [];
          if (messages.some((m) => m.id === message.id)) {
            return state;
          }
          return {
            chatHistory: {
              ...state.chatHistory,
              [projectId]: [...messages, message]
            }
          };
        });
      },

      clearChatHistory: (projectId: string) => {
        set((state) => ({
          chatHistory: {
            ...state.chatHistory,
            [projectId]: []
          },
          loadingStates: {
            ...state.loadingStates,
            [projectId]: createDefaultLoadingState()
          }
        }));
      },

      removeLastMessage: (projectId: string) => {
        set((state) => {
          const messages = state.chatHistory[projectId] || [];
          if (messages.length === 0) return state;
          return {
            chatHistory: {
              ...state.chatHistory,
              [projectId]: messages.slice(0, -1)
            }
          };
        });
      },

      getLoadingState: (projectId: string) => {
        return get().loadingStates[projectId] || createDefaultLoadingState();
      },

      setLoadingState: (projectId: string, updates: Partial<PresentationChatLoadingState>) => {
        set((state) => {
          const currentState = state.loadingStates[projectId] || createDefaultLoadingState();
          return {
            loadingStates: {
              ...state.loadingStates,
              [projectId]: { ...currentState, ...updates }
            }
          };
        });
      },

      resetLoadingState: (projectId: string) => {
        set((state) => ({
          loadingStates: {
            ...state.loadingStates,
            [projectId]: createDefaultLoadingState()
          }
        }));
      },

      getMessagesWithPending: (projectId: string) => {
        const messages = get().getChatHistory(projectId);
        const loadingState = get().getLoadingState(projectId);
        if (loadingState.pendingBotMessage) {
          return [...messages, loadingState.pendingBotMessage];
        }
        return messages;
      }
    }),
    {
      name: LS_PRESENTATION_CHAT_STORAGE,
      skipHydration: true,
      partialize: (state) => ({
        chatHistory: state.chatHistory,
        loadingStates: state.loadingStates
      })
    }
  )
);

const MAX_CHAT_HISTORY_LENGTH = 20;

function getLastItems<T>(arr: T[], n: number): T[] {
  return arr.slice(-n);
}

type SlidesResult = {
  message: string;
  slidesCount?: number;
  slides: Array<{ title: string; content: string }>;
};

function parseSlidesResult(result: string | null | undefined): SlidesResult | null {
  if (!result) return null;
  try {
    const parsed = JSON.parse(result);
    if (parsed.slides && Array.isArray(parsed.slides)) {
      return parsed as SlidesResult;
    }
    return null;
  } catch {
    return null;
  }
}

export function usePresentationChat() {
  const { id: projectId } = useParams() as { id: string };
  const {
    getChatHistory,
    addMessage,
    clearChatHistory,
    removeLastMessage,
    getLoadingState,
    setLoadingState,
    resetLoadingState,
    getMessagesWithPending
  } = usePresentationChatStore();
  const {
    localPresentation,
    setPresentation,
    setLocalPresentation,
    markClean,
    setSlidesUpdatedFromChat
  } = usePresentationStore();
  const { updateSelectedProjectPresentation } = useProjectStore();

  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [isAbortingRequest, setIsAbortingRequest] = useState(false);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const requestInFlightRef = useRef(false);
  const processedTasksRef = useRef<Set<number>>(new Set());
  const hasPolledOnceRef = useRef(false);

  const loadingState = getLoadingState(projectId);
  const { loadingMessageId, pendingGenerationId } = loadingState;
  const isLoading = !!pendingGenerationId;

  const messages = getMessagesWithPending(projectId);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const pollGenerationStatus = useCallback(async () => {
    const currentLoadingState = getLoadingState(projectId);
    const currentPendingGenerationId = currentLoadingState.pendingGenerationId;

    if (requestInFlightRef.current || !currentPendingGenerationId) {
      return;
    }
    requestInFlightRef.current = true;

    try {
      const statuses = await getStatusAll(projectId, EGenerationTaskType.chatSlides);

      const slidesTask = statuses.find(
        (status) => status.id === currentPendingGenerationId
      );

      if (!slidesTask || processedTasksRef.current.has(slidesTask.id)) {
        return;
      }

      switch (slidesTask.status) {
        case EGenerationStatus.pending:
        case EGenerationStatus.processing:
        case EGenerationStatus.streaming:
          // Still processing
          break;

        case EGenerationStatus.ready: {
          await confirmPollingEnd(slidesTask.id);
          processedTasksRef.current.add(slidesTask.id);

          const slidesResult = parseSlidesResult(slidesTask.result);
          
          let completionMessageText = "Done";
          if (slidesResult?.message) {
            completionMessageText = slidesResult.message;
          } else if (slidesTask.result) {
            try {
              const parsed = JSON.parse(slidesTask.result);
              if (parsed.message && typeof parsed.message === "string") {
                completionMessageText = parsed.message;
              }
            } catch {
              // ignore
            }
          }

          if (slidesResult && slidesResult.slides.length > 0) {
            const updatedSlides: TSlide[] = slidesResult.slides.map((slide, index) => ({
              id: `slide-${Date.now()}-${index}`,
              title: slide.title,
              outline: slide.content,
              order: index + 1
            }));

            // Get current presentation or create a new one
            const currentLocalPresentation = localPresentation || {
              id: 0,
              projectId: parseInt(projectId),
              status: "completed" as const,
              gammaStatus: null,
              gammaWebUrl: null,
              gammaPptxUrl: null,
              gammaPdfUrl: null,
              slidesGeneratedAt: new Date().toISOString(),
              slidesUpdatedAt: new Date().toISOString(),
              gammaGeneratedAt: new Date().toISOString(),
              allowRegeneration: false,
              slides: []
            };

            const updatedPresentation: TPresentation = {
              ...currentLocalPresentation,
              slides: updatedSlides,
              slidesGeneratedAt: new Date().toISOString(),
              slidesUpdatedAt: new Date().toISOString(),
              allowRegeneration: true
            };

            // Mark that slides were updated from chat to prevent useEffect from overwriting
            setSlidesUpdatedFromChat(true);

            // Update presentation store
            setPresentation(updatedPresentation);
            console.log("Setting presentation", updatedPresentation);
            setLocalPresentation(updatedPresentation);
            console.log("Setting local presentation", updatedPresentation);
            markClean();

            // Update project store
            updateSelectedProjectPresentation({
              status: "completed",
              gammaWebUrl: null,
              gammaPptxUrl: null,
              slidesCount: updatedSlides.length
            });

            // Reset the flag after a short delay to allow useEffect to check it
            setTimeout(() => {
              setSlidesUpdatedFromChat(false);
            }, 1000);

            toast.success("Slides updated successfully!");
          } else {
            console.warn("[PRESENTATION_CHAT] No slides found in task result:", slidesTask.result);
          }

          // Add completion message as the assistant response
          const currentLoadingState = getLoadingState(projectId);
          const completionMessage: PresentationChatMessage = {
            role: "assistant",
            content: completionMessageText,
            id: currentLoadingState.loadingMessageId || `bot-${Date.now()}`
          };
          addMessage(projectId, completionMessage);

          // Reset loading state
          resetLoadingState(projectId);
          stopPolling();
          break;
        }

        case EGenerationStatus.error: {
          await confirmPollingEnd(slidesTask.id);
          processedTasksRef.current.add(slidesTask.id);

          // Add error message
          const currentLoadingState = getLoadingState(projectId);
          const errorMessage: PresentationChatMessage = {
            role: "assistant",
            content: JSON.stringify({
              type: "error",
              content: "Generation failed. Please try again."
            }),
            id: currentLoadingState.loadingMessageId || `bot-${Date.now()}`
          };
          addMessage(projectId, errorMessage);

          resetLoadingState(projectId);
          stopPolling();
          break;
        }

        case EGenerationStatus.cancelled:
          processedTasksRef.current.add(slidesTask.id);
          resetLoadingState(projectId);
          stopPolling();
          break;
      }
    } catch (error) {
      console.error("Error polling generation status:", error);
    } finally {
      requestInFlightRef.current = false;
    }
  }, [projectId, getLoadingState, addMessage, stopPolling, resetLoadingState, localPresentation, setPresentation, setLocalPresentation, markClean, setSlidesUpdatedFromChat, updateSelectedProjectPresentation]);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    pollGenerationStatus();
    pollingIntervalRef.current = setInterval(pollGenerationStatus, 2000);
  }, [pollGenerationStatus]);

  // Poll once on mount to check for active tasks (similar to ChatIsland)
  const pollOnce = useCallback(async () => {
    if (hasPolledOnceRef.current) return;
    hasPolledOnceRef.current = true;

    try {
      const currentLoadingState = getLoadingState(projectId);
      
      // If we have a stored pendingGenerationId, check if the task is still active
      if (currentLoadingState.pendingGenerationId) {
        const statuses = await getStatusAll(projectId, EGenerationTaskType.chatSlides);
        const slidesTask = statuses.find(
          (status) => status.id === currentLoadingState.pendingGenerationId
        );

        if (slidesTask) {
          // Restore pending bot message if we have loadingMessageId
          if (!currentLoadingState.pendingBotMessage && currentLoadingState.loadingMessageId) {
            setLoadingState(projectId, {
              pendingBotMessage: {
                role: "assistant",
                content: "",
                id: currentLoadingState.loadingMessageId
              }
            });
          }

          // If task is still active, start polling
          if (
            slidesTask.status === EGenerationStatus.pending ||
            slidesTask.status === EGenerationStatus.processing ||
            slidesTask.status === EGenerationStatus.streaming
          ) {
            startPolling();
          } else {
            // Task is completed/error/cancelled, process it immediately
            pollGenerationStatus();
          }
        } else {
          // Task not found, reset state
          resetLoadingState(projectId);
        }
      }
    } catch (error) {
      console.error("Error polling on mount:", error);
    }
  }, [projectId, getLoadingState, setLoadingState, resetLoadingState, startPolling, pollGenerationStatus]);

  // Poll once on mount
  useEffect(() => {
    pollOnce();
  }, [pollOnce]);

  // Start polling when we have a pending generation
  useEffect(() => {
    if (pendingGenerationId) {
      startPolling();
    } else {
      stopPolling();
    }
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGenerationId]);

  const handleSubmit = async (messageText: string) => {
    if (!projectId || !messageText.trim() || isLoading) return;

    const userMessage: PresentationChatMessage = {
      role: "user",
      content: messageText,
      id: `user-${Date.now()}`
    };

    addMessage(projectId, userMessage);

    const botMessageId = `bot-${Date.now()}`;
    const botLoadingMessage: PresentationChatMessage = {
      role: "assistant",
      content: "",
      id: botMessageId
    };

    try {
      setIsSendingRequest(true);
      setLoadingState(projectId, {
        loadingMessageId: botMessageId,
        pendingBotMessage: botLoadingMessage,
        pendingGenerationId: null
      });

      const messagesHistory = getLastItems(
        getChatHistory(projectId).map((m) => ({ role: m.role, content: m.content })),
        MAX_CHAT_HISTORY_LENGTH
      );

      const response = await $api.post<{ generationId: number }>(
        `/projects/${projectId}/presentations/chat`,
        {
          content: messageText,
          messagesHistory,
          model: null
        }
      );

      setLoadingState(projectId, {
        pendingGenerationId: response.data.generationId
      });
    } catch (error) {
      console.error("Error starting generation:", error);

      const errorMessage: PresentationChatMessage = {
        role: "assistant",
        content: JSON.stringify({
          type: "error",
          content: "Failed to send message. Please try again."
        }),
        id: botMessageId
      };
      addMessage(projectId, errorMessage);

      resetLoadingState(projectId);
    } finally {
      setIsSendingRequest(false);
    }
  };

  const handleAbortRequest = async () => {
    const currentLoadingState = getLoadingState(projectId);
    if (!currentLoadingState.pendingGenerationId) return;

    setIsAbortingRequest(true);
    try {
      await $api.post("/chat/cancel", { generationId: currentLoadingState.pendingGenerationId });
      removeLastMessage(projectId);
    } catch (error) {
      console.error("Failed to cancel generation:", error);
    } finally {
      setIsAbortingRequest(false);
      resetLoadingState(projectId);
      stopPolling();
    }
  };

  const handleClearChat = () => {
    clearChatHistory(projectId);
  };

  return {
    messages,
    hasMessages: getChatHistory(projectId).length > 0,
    isLoading,
    isSendingRequest,
    isAbortingRequest,
    loadingMessageId,
    handleSubmit,
    handleAbortRequest,
    handleClearChat
  };
}
